import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, InputNumber, Switch, Button, Space, Card, Row, Col, Divider, Typography, Upload, App } from 'antd';
import { blogService } from '../services/blogService';

const { TextArea } = Input;

const BlogForm = () => {
  const { message: msg } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const isEdit = Boolean(id);
  const [uploadingCover, setUploadingCover] = useState(false);

  const normalizeTags = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string') {
      const raw = value.trim();
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
      } catch (_) {}
      return raw
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
    }
    return [];
  };

  useEffect(() => {
    const load = async () => {
      if (!isEdit) return;
      try {
        setLoading(true);
        const data = await blogService.getById(id);
        form.setFieldsValue({
          title: data.title,
          slug: data.slug,
          excerpt: data.excerpt,
          content: data.content,
          videoUrl: data.videoUrl,
          coverImageUrl: data.coverImageUrl,
          tags: normalizeTags(data.tags).join(', '),
          isPublished: data.isPublished,
          displayOrder: data.displayOrder ?? null,
        });
      } catch (err) {
        message.error(err?.response?.data?.message || "Impossible de charger l'article");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const onFinish = async (values) => {
    const payload = {
      ...values,
      tags: (values.tags || '')
        .split(',')
        .map(t => t.trim())
        .filter(Boolean),
    };
    try {
      setLoading(true);
      if (isEdit) {
        await blogService.update(id, payload);
        msg.success('Article mis à jour');
      } else {
        await blogService.create(payload);
        msg.success('Article créé');
      }
      navigate('/admin/blogs');
    } catch (err) {
      message.error('Enregistrement impossible');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page blog-form-page" style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: '#f5f5f5', padding: '12px 0', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>{isEdit ? 'Modifier l\'article' : 'Nouvel article'}</Typography.Title>
          <Space>
            <Button onClick={() => navigate('/admin/blogs')}>Annuler</Button>
            <Button type="primary" onClick={() => form.submit()} loading={loading}>{isEdit ? 'Enregistrer' : 'Publier'}</Button>
          </Space>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish} disabled={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card bodyStyle={{ padding: 16 }}>
              <Form.Item label="Titre" name="title" rules={[{ required: true, message: 'Le titre est requis' }]}>
                <Input size="large" placeholder="Titre de l'article" />
              </Form.Item>

              <Form.Item label="Slug" name="slug" tooltip="Laissez vide pour auto-générer">
                <Input placeholder="ex: presentation-domoelec" />
              </Form.Item>

              <Divider style={{ margin: '12px 0' }}>Résumé</Divider>
              <Form.Item name="excerpt">
                <TextArea rows={3} placeholder="Résumé court (affiché dans les listes)" />
              </Form.Item>

              <Divider style={{ margin: '12px 0' }}>Contenu</Divider>
              <Form.Item name="content">
                <TextArea rows={12} placeholder="Contenu en Markdown ou HTML" />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Space direction="vertical" style={{ width: '100%' }} size={16}>
              <Card title="Publication" bodyStyle={{ padding: 16 }}>
                <Form.Item label="Publié" name="isPublished" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Switch />
                </Form.Item>

                <Divider style={{ margin: '12px 0' }} />
                <Form.Item
                  label="Position (1-60)"
                  name="displayOrder"
                  tooltip="Permet de choisir la page: 1-6 = page 1, 7-12 = page 2, ... jusqu'a 10 pages."
                >
                  <InputNumber min={1} max={60} style={{ width: '100%' }} placeholder="Ex: 1" />
                </Form.Item>
              </Card>

              <Card title="Médias" bodyStyle={{ padding: 16 }}>
                <Form.Item label="URL vidéo (YouTube embed)" name="videoUrl">
                  <Input placeholder="https://www.youtube.com/embed/xxxx" />
                </Form.Item>
                <Form.Item name="coverImageUrl" hidden>
                  <Input />
                </Form.Item>

                <Form.Item label="Uploader image de couverture">
                  <Upload.Dragger
                    name="images"
                    multiple={false}
                    maxCount={1}
                    accept="image/*"
                    customRequest={async ({ file, onSuccess, onError }) => {
                      try {
                        setUploadingCover(true);
                        const response = await blogService.uploadCoverImage(file);
                        const url = response?.url;
                        if (!url) {
                          throw new Error("URL d'image manquante");
                        }
                        form.setFieldsValue({ coverImageUrl: url });
                        msg.success('Image de couverture uploadee');
                        onSuccess?.(response);
                      } catch (error) {
                        const apiMessage =
                          error?.response?.data?.message ||
                          error?.message ||
                          "Erreur lors de l'upload";
                        message.error(apiMessage);
                        onError?.(error);
                      } finally {
                        setUploadingCover(false);
                      }
                    }}
                    showUploadList={true}
                    disabled={loading || uploadingCover}
                  >
                    <p className="ant-upload-drag-icon">
                      <i className="fas fa-cloud-upload-alt"></i>
                    </p>
                    <p className="ant-upload-text">
                      Glisse une image ici, ou clique pour sélectionner
                    </p>
                    <p className="ant-upload-hint">
                      JPG / PNG / WEBP • 10MB max
                    </p>
                  </Upload.Dragger>
                </Form.Item>
              </Card>

              <Card title="Référencement" styles={{ body: { padding: 16 } }}>
                <Form.Item label="Tags (séparés par des virgules)" name="tags">
                  <Input placeholder="sécurité, onduleur, incendie" />
                </Form.Item>
              </Card>
            </Space>
          </Col>
        </Row>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button onClick={() => navigate('/admin/blogs')}>Annuler</Button>
          <Button type="primary" htmlType="submit" loading={loading}>{isEdit ? 'Enregistrer' : 'Publier'}</Button>
        </div>
      </Form>
    </div>
  );
};

export default BlogForm;


